import React, { useEffect, useState } from "react";
import { Form, Button, Spinner, Container } from "react-bootstrap";
import { FiEye, FiEyeOff, FiLogOut, FiSave, FiSettings } from "react-icons/fi";
import { toast } from "react-toastify";
import BackendHeader from "../component/BackendHeader";
import axiosInstance from "../axiosInstance";
import { clearTradeChatConfigCache } from "../libs/tradeChatConfig";
import { fetchSystemSettingsPermissions } from "../libs/customerCreatePermission";
import { formatRevokedAt } from "./formatRevokedAt";
import "../css/adminSystemSettings.css";

const SECTION_GENERAL = "general";
const SECTION_API_KEYS = "api-keys";
const SECTION_ACCOUNT = "account";

const SECTIONS = [
  { id: SECTION_GENERAL, label: "Cài đặt chung" },
  { id: SECTION_API_KEYS, label: "API keys" },
  { id: SECTION_ACCOUNT, label: "Cài đặt tài khoản" },
];

function maskKey(value) {
  const key = String(value || "");
  if (!key) return "";
  if (key.length <= 6) return "*".repeat(key.length);
  return `${key.slice(0, 3)}${"*".repeat(key.length - 6)}${key.slice(-3)}`;
}

function keyView(state) {
  const canShow = state.revealAllowed || state.dirty;
  const maskedValue = state.dirty || state.revealAllowed ? maskKey(state.value) : state.masked;
  return {
    canShow,
    maskedValue,
    input: state.show && canShow ? state.value : maskedValue,
  };
}

function nextKeyState(state, raw) {
  const { canShow, maskedValue } = keyView(state);
  if (state.show && canShow) {
    return { ...state, value: raw, dirty: true };
  }
  if (!raw.includes("*")) {
    return { ...state, value: raw, dirty: true, show: true };
  }
  if (raw.length < maskedValue.length) {
    return { ...state, value: "", dirty: true, show: true };
  }
  return state;
}

const EMPTY_KEY = {
  value: "",
  masked: "",
  revealAllowed: false,
  dirty: false,
  show: false,
};

function keyFromResponse(full, masked, revealAllowed) {
  const canReveal = revealAllowed === true;
  return {
    value: canReveal ? String(full || "") : "",
    masked: String(masked || ""),
    revealAllowed: canReveal,
    dirty: false,
    show: false,
  };
}

function KeyField({ id, label, hint, state, onChange, onToggle }) {
  const view = keyView(state);
  const visible = state.show && view.canShow;
  return (
    <div className="admin-system-settings__key-block">
      <div className="admin-system-settings__key-row">
        <label htmlFor={id} className="form-label mb-0">
          {label}
        </label>
        <div className="admin-system-settings__key-field">
          <input
            id={id}
            className="form-control"
            value={view.input}
            onChange={onChange}
            autoComplete="off"
            spellCheck={false}
            placeholder="Nhập key"
          />
          <button
            type="button"
            className="admin-system-settings__key-toggle"
            onClick={onToggle}
            aria-label={visible ? "Ẩn key" : "Hiện key"}
            aria-pressed={visible}
          >
            {visible ? <FiEyeOff /> : <FiEye />}
          </button>
        </div>
      </div>
      <Form.Text className="d-block mt-2 text-muted">{hint}</Form.Text>
    </div>
  );
}

const AdminSystemSettings = () => {
  const [activeSection, setActiveSection] = useState(SECTION_GENERAL);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [revokedAtMillis, setRevokedAtMillis] = useState(null);
  const [serverSecurity, setServerSecurity] = useState(true);
  const [clientSecurity, setClientSecurity] = useState(false);
  const [checkRequestDomain, setCheckRequestDomain] = useState(false);
  const [publicKey, setPublicKey] = useState(EMPTY_KEY);
  const [portalKey, setPortalKey] = useState(EMPTY_KEY);
  const [access, setAccess] = useState({ loaded: false, canView: false, canEdit: false });

  const applySettings = (data) => {
    const mode = data?.tradeChatPortalSecurityMode;
    const isClient = String(mode || "server").toLowerCase() === "client";
    setServerSecurity(!isClient);
    setClientSecurity(isClient);
    setCheckRequestDomain(data?.checkRequestDomain === true);
    setPublicKey(keyFromResponse(
      data?.tradePublishKey,
      data?.tradePublishKeyMasked,
      data?.tradePublishKeyRevealAllowed
    ));
    setPortalKey(keyFromResponse(
      data?.tradePortalKey,
      data?.tradePortalKeyMasked,
      data?.tradePortalKeyRevealAllowed
    ));
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/api/admin/system-settings");
      applySettings(res?.data?.data);
    } catch (err) {
      const msg = err?.response?.data?.message || "Không tải được cài đặt hệ thống.";
      toast.error(msg, { toastId: "system-settings-load-error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const permission = await fetchSystemSettingsPermissions(axiosInstance);
      if (cancelled) return;
      setAccess({
        loaded: true,
        canView: Boolean(permission?.canView),
        canEdit: Boolean(permission?.canEdit),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!access.loaded || !access.canView) return;
    loadSettings();
  }, [access.loaded, access.canView]);

  useEffect(() => {
    if (!access.loaded || !access.canView) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get("/api/admin/session-revoke-at");
        if (!cancelled) {
          setRevokedAtMillis(res?.data?.data?.revokedAtMillis || null);
        }
      } catch {
        // Thời điểm thu hồi không chặn phần còn lại của màn hình.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [access.loaded, access.canView]);

  const handleServerSecurityChange = (event) => {
    if (!event.target.checked) return;
    setServerSecurity(true);
    setClientSecurity(false);
  };

  const handleClientSecurityChange = (event) => {
    if (!event.target.checked) return;
    setClientSecurity(true);
    setServerSecurity(false);
  };

  const basePayload = () => ({
    tradeChatPortalSecurityMode: clientSecurity ? "client" : "server",
    checkRequestDomain,
  });

  const saveSettings = async (extra) => {
    setSaving(true);
    try {
      const res = await axiosInstance.put("/api/admin/system-settings", {
        ...basePayload(),
        ...extra,
      });
      if (!res?.data?.success) {
        throw new Error(res?.data?.message || "Không lưu được cài đặt.");
      }
      applySettings(res.data.data);
      clearTradeChatConfigCache();
      toast.success(res.data.message || "Lưu cài đặt thành công.");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Không lưu được cài đặt.");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = (event) => {
    event.preventDefault();
    if (!access.canEdit || activeSection === SECTION_ACCOUNT) return;
    if (activeSection === SECTION_API_KEYS) {
      saveSettings({
        tradePublishKey: publicKey.dirty ? publicKey.value.trim() : null,
        tradePortalKey: portalKey.dirty ? portalKey.value.trim() : null,
      });
      return;
    }
    saveSettings({});
  };

  const toggleKey = (state, setState) => {
    if (!(state.revealAllowed || state.dirty)) {
      toast.info("Chỉ người nhập key mới xem được đầy đủ key.");
      return;
    }
    setState({ ...state, show: !state.show });
  };

  const handleRevokeAllSessions = async () => {
    const confirmed = window.confirm(
      "Thu hồi mọi phiên đăng nhập? Những người khác sẽ phải đăng nhập lại. Phiên của bạn được giữ."
    );
    if (!confirmed) return;
    setRevoking(true);
    try {
      const res = await axiosInstance.post("/api/admin/revoke-all-sessions");
      if (!res?.data?.success) {
        throw new Error(res?.data?.message || "Không thu hồi được phiên đăng nhập.");
      }
      const token = res?.data?.data?.token;
      if (token) {
        localStorage.setItem("token", token);
      }
      setRevokedAtMillis(res?.data?.data?.revokedAtMillis || null);
      toast.success(res.data.message || "Đã thu hồi mọi phiên đăng nhập.");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Không thu hồi được phiên đăng nhập."
      );
    } finally {
      setRevoking(false);
    }
  };

  if (!access.loaded) {
    return (
      <div className="container-fluid p-0 web-cover">
        <BackendHeader />
        <Container className="py-5 text-center admin-system-settings__gate">
          <Spinner animation="border" role="status" />
        </Container>
      </div>
    );
  }

  if (!access.canView) {
    return (
      <div className="container-fluid p-0 web-cover">
        <BackendHeader />
        <Container className="py-4 admin-system-settings__gate">
          <div className="alert alert-danger mb-0">Access denied</div>
        </Container>
      </div>
    );
  }

  const activeLabel = SECTIONS.find((item) => item.id === activeSection)?.label || "";

  return (
    <div className="container-fluid p-0 web-cover">
      <BackendHeader />
      <Container className="py-4 admin-system-settings">
        <h1 className="admin-system-settings__title mb-4">Cài đặt hệ thống</h1>

        <div className="admin-system-settings__layout">
          <aside className="admin-system-settings__side">
            <header className="admin-system-settings__column-header">Hạng mục</header>
            <nav className="admin-system-settings__nav" aria-label="Hạng mục cài đặt">
              {SECTIONS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`admin-system-settings__nav-item${
                    activeSection === item.id ? " is-active" : ""
                  }`}
                  onClick={() => setActiveSection(item.id)}
                  aria-current={activeSection === item.id ? "page" : undefined}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </aside>

          <Form className="admin-system-settings__panel" onSubmit={handleSave}>
            <header className="admin-system-settings__column-header">{activeLabel}</header>
            <div className="admin-system-settings__panel-body">
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status" />
                </div>
              ) : activeSection === SECTION_GENERAL ? (
                <>
                  <section className="admin-system-settings__section">
                    <h2 className="h5 mb-3 d-flex align-items-center gap-2">
                      <FiSettings aria-hidden />
                      Tích hợp AI Double Boss
                    </h2>
                    <p className="text-muted small mb-3">
                      Chọn cách lấy session portal CSKH. Hai chế độ loại trừ lẫn nhau.
                    </p>
                    <Form.Check
                      type="checkbox"
                      id="trade-security-server"
                      className="mb-2"
                      label="Bảo mật server"
                      checked={serverSecurity}
                      onChange={handleServerSecurityChange}
                    />
                    <Form.Text className="d-block mb-3 ms-4 text-muted">
                      Portal key chỉ dùng trên server ZTO; trình duyệt gọi{" "}
                      <code>/api/admin/trade-portal-session</code>.
                    </Form.Text>
                    <Form.Check
                      type="checkbox"
                      id="trade-security-client"
                      className="mb-2"
                      label="Bảo mật client"
                      checked={clientSecurity}
                      onChange={handleClientSecurityChange}
                    />
                    <Form.Text className="d-block mb-0 ms-4 text-muted">
                      Client lấy Portal key và portal domain từ server, tích hợp portal giống{" "}
                      <code>portal.html</code> (mock fetch tại{" "}
                      <code>/api/admin/trade-portal-session</code>).
                    </Form.Text>
                  </section>
                  <section className="admin-system-settings__section mt-4">
                    <h2 className="h5 mb-3">Kiểm tra domain</h2>
                    <Form.Check
                      type="checkbox"
                      id="check-request-domain"
                      className="mb-2"
                      label="Chỉ cho phép domain hiện tại"
                      checked={checkRequestDomain}
                      onChange={(event) => setCheckRequestDomain(event.target.checked)}
                    />
                    <Form.Text className="d-block ms-4 text-muted">
                      Mặc định tắt. Khi bật, trình duyệt chỉ gọi được API từ đúng domain của trang
                      đang mở.
                    </Form.Text>
                  </section>
                </>
              ) : activeSection === SECTION_ACCOUNT ? (
                <section className="admin-system-settings__section">
                  <h2 className="h5 mb-3 d-flex align-items-center gap-2">
                    <FiLogOut aria-hidden />
                    Phiên đăng nhập
                  </h2>
                  <p className="text-muted small mb-3">
                    Thu hồi mọi phiên đang mở, gồm đăng nhập mật khẩu và Google.
                    Những người khác phải đăng nhập lại. Phiên của bạn được giữ.
                    Tài khoản và mật khẩu không bị đổi.
                  </p>
                  <p className="mb-3">
                    Thời gian thu hồi phiên đăng nhập:{" "}
                    <strong>{formatRevokedAt(revokedAtMillis) || "Chưa thu hồi"}</strong>
                  </p>
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    disabled={revoking || !access.canEdit}
                    onClick={handleRevokeAllSessions}
                    className="d-inline-flex align-items-center gap-2 px-4"
                  >
                    {revoking ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        Đang thu hồi…
                      </>
                    ) : (
                      <>
                        <FiLogOut size={18} aria-hidden />
                        Thu hồi mọi phiên
                      </>
                    )}
                  </Button>
                </section>
              ) : (
                <section className="admin-system-settings__section">
                  <KeyField
                    id="customer-chat-public-key"
                    label="Public key"
                    hint="Dùng cho kết nối chức năng chat của khách hàng."
                    state={publicKey}
                    onChange={(event) => setPublicKey((current) => nextKeyState(current, event.target.value))}
                    onToggle={() => toggleKey(publicKey, setPublicKey)}
                  />
                  <KeyField
                    id="portal-key"
                    label="Portal key"
                    hint="Dùng cho kết nối portal."
                    state={portalKey}
                    onChange={(event) => setPortalKey((current) => nextKeyState(current, event.target.value))}
                    onToggle={() => toggleKey(portalKey, setPortalKey)}
                  />
                  <Form.Text className="d-block mt-3 text-muted">
                    Key hiển thị 3 ký tự đầu và 3 ký tự cuối. Chỉ người nhập key mới xem được đầy đủ.
                  </Form.Text>
                </section>
              )}
            </div>
            {activeSection !== SECTION_ACCOUNT && (
            <footer className="admin-system-settings__panel-footer">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={saving || loading || !access.canEdit}
                className="d-inline-flex align-items-center gap-2 px-4"
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    Đang lưu…
                  </>
                ) : (
                  <>
                    <FiSave size={18} aria-hidden />
                    Lưu
                  </>
                )}
              </Button>
            </footer>
            )}
          </Form>
        </div>
      </Container>
    </div>
  );
};

export default AdminSystemSettings;
